import { useUsersApi } from "@/Hooks/useUsersApi";
import { refusalMessage } from "@/Helpers/refusalMessage";
import { CopyableLinkBox } from "@/Shared/Components/CustomComponents/CopyableLinkBox";
import Animation from "@/Shared/Animations/Animation";
import YellowExclamationAnimatedIcon from "@/Shared/Animations/YellowExclamationAnimatedIcon";

import type { DialogBaseProps } from "@freightsimple/ui-core/Dialog/DialogBaseProps";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Banner } from "@freightsimple/ui-core/Display/Banner";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { CopyToClipboardButton } from "@freightsimple/ui-core/Buttons/CopyToClipboardButton";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { FormItem } from "@freightsimple/ui-core/FormLayout/FormItem";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { ShowFormErrors } from "@freightsimple/ui-core/FormLayout/ShowFormErrors";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { TextInput } from "@freightsimple/ui-core/Controls/TextInput";
import { useAuthentication } from "@/Slices/authenticationSlice";
import {
  errorMessageForContactName,
  errorMessageForEmail,
} from "@/Shared/Contacts/Validators/contactsErrorMessagesForContact";
import { ResponseError } from "@freightsimple/generated-dashboard-openapi-client/runtime";
import { useEffect, useState } from "react";

interface ViewCompanyInviteDialogProps extends DialogBaseProps {
  /** Fired when the user closes the success view — the caller reloads. */
  onInviteSent: () => void;
}

// Invite-a-teammate dialog: collects name/email, sends the invitation, then
// shows the copyable invitation link. Replaces the legacy imperative modal.
export function ViewCompanyInviteDialog(props: ViewCompanyInviteDialogProps) {
  const usersApi = useUsersApi();
  const { user } = useAuthentication();
  const companyName = user?.companyName ?? "";
  const [inviteeEmailAddress, setInviteeEmailAddress] = useState("");
  const [inviteeName, setInviteeName] = useState("");
  const [forceValidation, setForceValidation] = useState(false);

  const [wasSuccess, setWasSuccess] = useState(false);
  const [url, setUrl] = useState("");

  const errorMessageForInviteeEmailAddress =
    errorMessageForEmail(inviteeEmailAddress);
  const errorMessageForInviteeName = errorMessageForContactName(inviteeName);
  const [loading, setLoading] = useState(false);
  const [isInvitationAlreadySent, setIsInvitationAlreadySent] = useState(false);
  // A failure the form can't point at a field for (a 5xx, a dropped
  // connection): shown above the form so the click doesn't look ignored.
  const [sendFailed, setSendFailed] = useState(false);
  // The server rejected the address as invalid (e.g. a non-ASCII local part Postmark
  // can't send to) after it passed the client-side regex. Shown as a FormItem error
  // alongside any client-side one, since the two never fire on the same attempt.
  const [serverEmailAddressError, setServerEmailAddressError] = useState<
    string | undefined
  >(undefined);

  const isValid =
    errorMessageForInviteeName === undefined &&
    errorMessageForInviteeEmailAddress === undefined;

  useEffect(() => {
    setIsInvitationAlreadySent(false);
    setServerEmailAddressError(undefined);
    setSendFailed(false);
  }, [inviteeEmailAddress]);

  async function resendInvitation() {
    setLoading(true);
    setSendFailed(false);
    try {
      const response = await usersApi.resendInvitation({
        resendInvitationRequest: {
          inviteeEmailAddress: inviteeEmailAddress,
        },
      });
      setUrl(response.invitationUrl);
      setWasSuccess(true);
    } catch (e) {
      console.error("Something went wrong resending", { e });
      setSendFailed(true);
    }
    setLoading(false);
  }

  async function onSendInvitation() {
    setForceValidation(true);

    if (!isValid) {
      return;
    }

    try {
      setLoading(true);
      setSendFailed(false);
      const response = await usersApi.sendInvitation({
        sendInvitationRequest: {
          inviteeEmailAddress,
          inviteeName,
        },
      });
      setUrl(response.invitationUrl);
      setWasSuccess(true);
    } catch (e) {
      console.error("Something went wrong requesting the invitation", {
        e,
      });

      if (e instanceof ResponseError && e.response.status === 409) {
        setIsInvitationAlreadySent(true);
      } else if (e instanceof ResponseError && e.response.status === 400) {
        setServerEmailAddressError(
          (await refusalMessage(e)) ?? "That email address isn't valid",
        );
      } else {
        setSendFailed(true);
      }
    }
    setLoading(false);
  }

  function done() {
    props.onOpenChange?.(false);
    props.onInviteSent();
  }

  if (wasSuccess) {
    return (
      <Dialog
        open={props.open}
        onOpenChange={props.onOpenChange}
        title="Invite Email Sent"
        description="Please ask the invitee to check their inbox"
        width="medium"
        footer={
          <Button primary size="large" onClick={done}>
            Done
          </Button>
        }
      >
        <ControlPresentationProvider presentation="underlined">
          <Stack align="center" gap={2}>
            <T size="sm" color="muted" align="center">
              (and their spam folder if they can&apos;t find it at first)
            </T>
            <Animation
              pathToAnimation="/animations/email-sent.json"
              lottieFilesUrl="https://lottiefiles.com/6391-email-sent"
              loop={true}
              speed={0.5}
            />
            <T size="base" color="secondary" align="center">
              Alternatively, you can copy this url and send it to them yourself
            </T>
            {/* The box carries the link and the button carries the affordance —
                a bare value with a trailing copy icon read as prose and the
                icon went unnoticed. Same pair as the other share dialogs. The
                id stays because the dashboard smoke tests read the invitation
                link out of `#invitationUrl`. */}
            <Row gap={3} shrinkable>
              <CopyableLinkBox url={url} id="invitationUrl" />
              <CopyToClipboardButton
                data={url}
                label="Invitation link"
                size="link"
              >
                Click here to copy to clipboard
              </CopyToClipboardButton>
            </Row>
          </Stack>
        </ControlPresentationProvider>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      title={`Invite someone to join ${companyName}`}
      description="Enter the details of someone you would like to receive an invite to join this company in FreightSimple. They will receive an email with instructions on how to create their account."
      width="medium"
      footer={
        <>
          <Button size="large" onClick={() => props.onOpenChange?.(false)}>
            Cancel
          </Button>
          {isInvitationAlreadySent ? (
            <Button
              primary
              size="large"
              onClick={resendInvitation}
              loading={loading}
            >
              Resend Invitation
            </Button>
          ) : (
            <Button
              primary
              size="large"
              onClick={onSendInvitation}
              loading={loading}
              data-testid="send-invitation-submit"
            >
              Send Invitation
            </Button>
          )}
        </>
      }
    >
      <ControlPresentationProvider presentation="underlined">
        <Stack align="center" gap={8}>
          {sendFailed && (
            <Banner variant="danger">
              We couldn&apos;t send the invitation. Please try again.
            </Banner>
          )}
          <ShowFormErrors show={forceValidation}>
            <Stack align="start" gap={4}>
              <FormItem
                label="Email"
                error={
                  errorMessageForInviteeEmailAddress ?? serverEmailAddressError
                }
              >
                <TextInput
                  type="text"
                  value={inviteeEmailAddress}
                  onChange={(e) => setInviteeEmailAddress(e.target.value)}
                  width="xl"
                  autoComplete="email"
                  name="invitee_email_address"
                />
              </FormItem>
              <FormItem label="Full Name" error={errorMessageForInviteeName}>
                <TextInput
                  type="text"
                  value={inviteeName}
                  onChange={(e) => setInviteeName(e.target.value)}
                  width="xl"
                  autoComplete="name"
                  name="invitee_full_name"
                />
              </FormItem>
              {isInvitationAlreadySent && (
                <Row gap={2}>
                  <YellowExclamationAnimatedIcon size={40} />
                  <T>
                    Invitation to <T weight="medium">{inviteeEmailAddress}</T>{" "}
                    already sent!
                  </T>
                </Row>
              )}
            </Stack>
          </ShowFormErrors>
        </Stack>
      </ControlPresentationProvider>
    </Dialog>
  );
}
